import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  LayoutDashboard, 
  Video, 
  Users, 
  Mic, 
  Calendar,
  Settings,
  FileText,
  ChevronRight,
  ChevronLeft,
  ShieldCheck,
} from 'lucide-react';

const iconMap = {
  dashboard: LayoutDashboard,
  meetings: Video,
  speakers: Mic,
  users: Users,
  admins: ShieldCheck,
  logs: FileText,
  calendar: Calendar,
  settings: Settings,
};

const Sidebar = ({ items, open, onClose }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = React.useState(null);
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  // Close sidebar on route change (mobile)
  React.useEffect(() => {
    if (onClose) onClose();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const getIcon = (label) => {
    const lowerLabel = label.toLowerCase();
    if (lowerLabel.includes('meeting')) return iconMap.meetings;
    if (lowerLabel.includes('speaker')) return iconMap.speakers;
    if (lowerLabel.includes('admin')) return iconMap.admins;
    if (lowerLabel.includes('log') || lowerLabel.includes('audit') || lowerLabel.includes('activity')) return iconMap.logs;
    if (lowerLabel.includes('user')) return iconMap.users;
    if (lowerLabel.includes('dashboard')) return iconMap.dashboard;
    if (lowerLabel.includes('calendar')) return iconMap.calendar;
    if (lowerLabel.includes('setting')) return iconMap.settings;
    return Video;
  };

  const sidebarStyle = {
    width: isCollapsed ? '70px' : '260px',
    minWidth: isCollapsed ? '70px' : '260px',
    height: '100vh',
    backgroundColor: theme.colors.sidebarBg,
    borderRight: `1px solid ${theme.colors.border}`,
    padding: isCollapsed ? '12px 8px' : '12px 0',
    position: 'sticky',
    top: 0,
    overflowY: 'auto',
    overflowX: 'visible',
    transition: 'width 0.25s ease, min-width 0.25s ease, transform 0.25s ease',
    zIndex: 300,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'flex-start',
  };

  // On mobile, sidebar is fixed/absolute and toggled by `open`
  const mobileSidebarStyle = {
    ...sidebarStyle,
    position: 'fixed',
    top: '60px',
    left: 0,
    height: 'calc(100vh - 60px)',
    width: '240px',
    minWidth: '240px',
    transform: open ? 'translateX(0)' : 'translateX(-100%)',
    boxShadow: open ? theme.shadows['2xl'] : 'none',
  };

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hide-mobile" style={sidebarStyle}>
        <SidebarContent
          items={items}
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          theme={theme}
          navigate={navigate}
          location={location}
          hoveredItem={hoveredItem}
          setHoveredItem={setHoveredItem}
          getIcon={getIcon}
        />
      </div>

      {/* Mobile sidebar (overlay) */}
      <div className="show-mobile" style={{ display: 'none' }}>
        {open && (
          <div
            style={{
              position: 'fixed', inset: 0, top: '60px',
              background: 'rgba(15, 23, 42, 0.5)',
              zIndex: 199,
              animation: 'fadeIn 0.2s ease',
            }}
            onClick={onClose}
          />
        )}
        <div style={mobileSidebarStyle}>
          <SidebarContent
            items={items}
            isCollapsed={false}
            setIsCollapsed={() => {}}
            theme={theme}
            navigate={navigate}
            location={location}
            hoveredItem={hoveredItem}
            setHoveredItem={setHoveredItem}
            getIcon={getIcon}
            hiddenCollapseBtn
          />
        </div>
      </div>
    </>
  );
};

const SidebarContent = ({
  items, isCollapsed, setIsCollapsed, theme, navigate, location,
  hoveredItem, setHoveredItem, getIcon, hiddenCollapseBtn,
}) => (
  <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: isCollapsed ? '8px' : '12px 0' }}>
    {/* Logo and Title */}
    <div
      style={{
        display: 'flex',
        flexDirection: isCollapsed ? 'column-reverse' : 'row',
        alignItems: 'center',
        justifyContent: isCollapsed ? 'center' : 'space-between',
        gap: '8px',
        padding: isCollapsed ? '8px' : '12px 16px',
        marginTop: '-4px',
        marginBottom: '14px',
        borderRadius: '10px',
        transition: 'background-color 0.2s',
        position: 'relative',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          overflow: 'hidden',
          cursor: 'pointer',
          borderRadius: '8px',
          padding: isCollapsed ? '0' : '4px 6px',
          flex: isCollapsed ? '0 0 auto' : 1,
        }}
        onClick={() => navigate('/')}
        onMouseEnter={(e) => !isCollapsed && (e.currentTarget.style.backgroundColor = theme.colors.backgroundSecondary)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        <div style={{
          width: isCollapsed ? '36px' : '40px',
          height: isCollapsed ? '36px' : '40px',
          background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryHover})`,
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 2px 8px ${theme.colors.primary}40`,
          flexShrink: 0,
        }}>
          <Video size={isCollapsed ? 18 : 20} color="#ffffff" />
        </div>
        {!isCollapsed && (
          <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <span style={{ fontSize: '15px', fontWeight: '700', color: theme.colors.text, letterSpacing: '-0.3px' }}>
              Conference
            </span>
            <span style={{ fontSize: '11px', color: theme.colors.textSecondary, fontWeight: '500' }}>
              Platform
            </span>
          </div>
        )}
      </div>

      {!hiddenCollapseBtn && (
        <button
          style={{
            width: isCollapsed ? '24px' : '30px',
            height: isCollapsed ? '24px' : '30px',
            borderRadius: '8px',
            border: `1px solid ${theme.colors.border}`,
            backgroundColor: 'transparent',
            color: theme.colors.textSecondary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
            transition: 'all 0.2s',
            position: 'static',
          }}
          onClick={() => setIsCollapsed(!isCollapsed)}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = theme.colors.backgroundSecondary;
            e.currentTarget.style.color = theme.colors.text;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = theme.colors.textSecondary;
          }}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      )}
    </div>

    {/* Nav label */}
    {!isCollapsed && (
      <div style={{
        fontSize: '10px', fontWeight: '700', textTransform: 'uppercase',
        letterSpacing: '0.8px', color: theme.colors.textTertiary,
        marginBottom: '6px', padding: '0 20px',
      }}>
        Navigation
      </div>
    )}

    {/* Nav items */}
    <div style={{ padding: isCollapsed ? '0 6px' : '0 10px', flex: 1, overflow: 'visible' }}>
      {items.map((item) => {
        const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
        const Icon = item.icon || getIcon(item.label);
        const isHovered = hoveredItem === item.path;

        return (
          <div
            key={item.path}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: isCollapsed ? 'center' : 'space-between',
              padding: isCollapsed ? '11px' : '10px 14px',
              marginBottom: '3px',
              color: isActive ? theme.colors.primary : (isHovered ? theme.colors.text : theme.colors.textSecondary),
              backgroundColor: isActive
                ? `${theme.colors.primary}12`
                : isHovered ? theme.colors.backgroundSecondary : 'transparent',
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'all 0.15s',
              fontSize: '14px',
              fontWeight: isActive ? '600' : '500',
              border: isActive ? `1px solid ${theme.colors.primary}25` : '1px solid transparent',
              position: 'relative',
            }}
            onClick={() => navigate(item.path)}
            onMouseEnter={() => setHoveredItem(item.path)}
            onMouseLeave={() => setHoveredItem(null)}
            title={isCollapsed ? item.label : undefined}
            aria-label={isCollapsed ? item.label : undefined}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              gap: isCollapsed ? '0' : '10px',
              overflow: isCollapsed ? 'visible' : 'hidden',
              width: '100%',
            }}>
              <Icon
                size={20}
                strokeWidth={isActive ? 2.4 : 2.2}
                color={isActive ? theme.colors.primary : theme.colors.textSecondary}
                style={{ minWidth: '20px', minHeight: '20px', flexShrink: 0 }}
              />
              {!isCollapsed && (
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.label}
                </span>
              )}
            </div>
            {isActive && !isCollapsed && (
              <ChevronRight size={14} style={{ opacity: 0.6, flexShrink: 0 }} />
            )}
          </div>
        );
      })}
    </div>
  </div>
);

export default Sidebar;
